/**
 * THE CONSOLE'S LAYOUT, THE PURE HALF (prototype v2, 2026-11-02): how a person moves between screens.
 *
 * Hamzah, 2026-11-02, of the v2 prototype: "copy the settings first so we can apply that and check".
 * The prototype carries its navigation as a SETTING (Settings › Appearance › Layout, `settings.nav`):
 * a top bar, a sidebar, a floating dock, the two together, or nothing but Apps and ⌘K for a wall. This
 * file is that setting's vocabulary, its two named states and the rules every layout shares, kept
 * apart from the store (`layout-store.ts`) and the shell so they can be tested without a document.
 *
 * TODAY'S SIDEBAR IS THE DEFAULT (`LAYOUT_DEFAULTS`): nothing a person sees changes until somebody
 * picks a layout. `LAYOUT_V2` (`layout-nav-logic.ts`, the lazy half) is the prototype's own defaults, which Settings applies in one press.
 *
 * ONE REACHABILITY RULE FOR EVERY LAYOUT. Whatever draws the navigation — the rail, the Apps launcher,
 * ⌘K, the bottom bar — draws it from `NAV_GROUPS` minus the shell's `hiddenItems`, which is how the
 * rail has always been gated (`App.tsx`). A layout changes WHERE a screen is offered, never WHETHER a
 * role may reach it: `layout-logic.test.ts` holds every layout to the rail's own set.
 */

/** `settings.nav` in the prototype (`js/core.js`), its five values. */
export type NavLayout = 'sidebar' | 'top' | 'topside' | 'floating' | 'minimal';
/**
 * The sidebar's edge. `auto` is JAZZ's own rule before the setting existed: the Glass look floats the
 * rail one gap in from the window (`glass.css`), Solid sets it against the edge. The prototype's two
 * are explicit choices on top of it.
 */
export type SideStyle = 'auto' | 'floating' | 'attached';
export type TopBarMode = 'shown' | 'autohide';
export type DockPos = 'bottom' | 'left' | 'right';

export interface LayoutSettings {
  nav: NavLayout;
  sideStyle: SideStyle;
  /** Whether the sidebar starts collapsed. Applied to `useNav` (`theme.ts`), which owns the rail's width. */
  sideCollapsed: boolean;
  topBar: TopBarMode;
  /** The dock's items, by nav id; `null` is the dock's own default set. */
  dockItems: readonly string[] | null;
  dockAutoHide: boolean;
  dockPos: DockPos;
  /** Screens pinned to the head of the Apps launcher, by nav id, in the order they were pinned. */
  pins: readonly string[];
}

/** Today's JAZZ, exactly: the rail, its look-dependent edge, nothing pinned. */
export const LAYOUT_DEFAULTS: Readonly<LayoutSettings> = {
  nav: 'sidebar',
  sideStyle: 'auto',
  sideCollapsed: false,
  topBar: 'shown',
  dockItems: null,
  dockAutoHide: false,
  dockPos: 'bottom',
  pins: [],
};



const NAVS: readonly NavLayout[] = ['sidebar', 'top', 'topside', 'floating', 'minimal'];

/** A stored record read back: anything unknown or malformed falls to today's default, key by key. */
export function sanitizeLayout(raw: unknown): LayoutSettings {
  const o = raw !== null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const ids = (v: unknown): string[] | null =>
    Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === 'string' && x.length > 0))] : null;
  return {
    nav: NAVS.includes(o.nav as NavLayout) ? (o.nav as NavLayout) : LAYOUT_DEFAULTS.nav,
    sideStyle: o.sideStyle === 'floating' || o.sideStyle === 'attached' ? o.sideStyle : 'auto',
    sideCollapsed: o.sideCollapsed === true,
    topBar: o.topBar === 'autohide' ? 'autohide' : 'shown',
    dockItems: ids(o.dockItems),
    dockAutoHide: o.dockAutoHide === true,
    dockPos: o.dockPos === 'left' || o.dockPos === 'right' ? o.dockPos : 'bottom',
    pins: ids(o.pins) ?? [],
  };
}

/**
 * Below this width every layout but today's sidebar becomes the bottom tab bar (the prototype: "Under
 * 900 px every navigation becomes the bottom tab bar"). The SIDEBAR keeps today's own narrow rule — the
 * rail collapsing to icons under 768 px (`theme.ts`, `useNav`) — because the default must not change
 * until somebody picks a layout.
 */
export const BOTTOM_BAR_MAX = 899;

/** Whether the layout draws the rail. */
export function hasSidebar(nav: NavLayout): boolean {
  return nav === 'sidebar' || nav === 'topside';
}

/** Whether the layout draws the main screens as underline tabs under the page title. */
export function hasMainTabs(nav: NavLayout): boolean {
  return nav === 'top' || nav === 'topside';
}

/**
 * Whether a screen is on the rail in this layout. Top bar + Sidebar puts the main screens on top and
 * "the rest in the sidebar" (the prototype's words), so the console screens the main tabs carry leave
 * the rail; they stay reachable there, in Apps and in ⌘K, and under 900 px in the bottom bar. The shell
 * draws the rail with it; `layout-nav-logic.ts`'s `railGroups` is the same rule over the groups, for the
 * tests that hold every layout to the rail's set.
 */
export function onRail(nav: NavLayout, id: string): boolean {
  return nav !== 'topside' || !MAIN_CONSOLE_TABS.includes(id);
}


/**
 * The console screens every kind of site names among its main screens (Overview, Alarms, Work
 * orders): the ones Top bar + Sidebar lifts out of the rail. `layout-nav-logic.ts`'s registry draws
 * its "no site" six from the same list, and its test holds the two together.
 */
export const MAIN_CONSOLE_TABS: readonly string[] = ['overview', 'alarms', 'work-orders'];

/**
 * A main tab reopening the hub (`layout-go.ts`, `App.tsx`). Here, in the eager half, so the app can
 * name it without loading the half that sends it.
 */
export const HUB_REOPEN = 'lm:hub-reopen';
